import type { SupabaseClient } from '@supabase/supabase-js';
import { ApiError } from './api.ts';

// Supabase MFA, wrapped so no screen touches the raw client.
//
// EVERY function takes the client as an argument rather than importing a
// singleton (spec D5). packages/ui serves two apps with two different Supabase
// clients — the vendor one and the admin one — so a singleton here would bind
// this module to whichever app imported it first. Admin adoption later is
// literally "pass getAdminSupabase()".
//
// Vocabulary, because the two questions are easy to conflate:
//   "enrolled"  — the USER holds a verified second factor. A property of the
//                 account, from listFactors().
//   "aal2"      — THIS SESSION has proven that factor. A property of the
//                 access token, from getAuthenticatorAssuranceLevel().
// A vendor can be enrolled and still be at aal1 on a brand-new session; that
// is exactly the step-up case. "Step up" means proving the factor to reach
// aal2, which is what companyAuth requires of every /api/me request (spec D6).
//
// The pure functions at the top hold the decisions, so they are testable
// without a DOM — this monorepo has no jsdom (spec §8).

export type FactorType = 'totp' | 'phone';

/** The subset of a Supabase factor these decisions read. */
export interface FactorLike {
  id: string;
  factor_type: string;
  status: string;
}

export interface MfaState {
  /** At least one VERIFIED totp/phone factor is attached to the account. */
  enrolled: boolean;
  verifiedFactorId: string | null;
  factorType: FactorType | null;
  /** This session has already proven a factor. */
  atAal2: boolean;
  /** Enrolled, but this session has not proven it yet. */
  needsStepUp: boolean;
}

export type GateDecision = 'enrol' | 'step-up' | 'pass';

const SUPPORTED: readonly string[] = ['totp', 'phone'];

/**
 * Fold a factor list and an assurance level into the state the gate reads.
 *
 * Unverified factors do not count: a vendor who starts enrolment and abandons
 * it must not be treated as protected. An unrecognised assurance level counts
 * as aal1 — failing closed, since reading an unknown value as "already proven"
 * would wave a session straight past the gate.
 */
export function deriveMfaState(
  factors: FactorLike[],
  currentLevel: string | null | undefined,
): MfaState {
  const verified =
    factors.find((f) => SUPPORTED.includes(f.factor_type) && f.status === 'verified') ?? null;
  const atAal2 = currentLevel === 'aal2';
  return {
    enrolled: verified !== null,
    verifiedFactorId: verified?.id ?? null,
    factorType: (verified?.factor_type as FactorType | undefined) ?? null,
    atAal2,
    needsStepUp: verified !== null && !atAal2,
  };
}

/** Which screen the vendor should see, if any. */
export function gateDecision(state: MfaState): GateDecision {
  if (!state.enrolled) return 'enrol';
  if (state.needsStepUp) return 'step-up';
  return 'pass';
}

/**
 * True only for companyAuth's aal2 refusal (spec D7).
 *
 * The shell reacts to this by refreshing the session rather than signing out —
 * so it must NOT match an ordinary 403. A deactivated vendor that got treated
 * as mfa_required would refresh in a loop instead of being signed out.
 */
export function isMfaRequiredError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    (err.details as { reason?: string } | null | undefined)?.reason === 'mfa_required'
  );
}

/** Seconds a Resend button should stay disabled after a send. */
export const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Whole seconds left before another SMS may be requested, 0 when free.
 *
 * Supabase rate-limits `challenge()`. Without a cooldown a vendor who taps
 * Resend twice earns a "Too many requests", which reads to them as the portal
 * being broken rather than as them being early. A backwards clock jump yields
 * the full window rather than a negative, so the button can never be enabled
 * by a clock correction.
 */
export function resendCooldownRemaining(sentAtMs: number | null, nowMs: number): number {
  if (sentAtMs === null) return 0;
  const elapsed = Math.floor((nowMs - sentAtMs) / 1000);
  if (elapsed < 0) return RESEND_COOLDOWN_SECONDS;
  return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
}

/**
 * What to say when a full code is submitted to a screen holding no challenge.
 *
 * This used to be a silent return: the vendor typed the code they had been
 * texted, nothing happened, and only a guessed press of Resend got them out.
 */
export function missingChallengeMessage(sending: boolean): string {
  return sending
    ? 'Your code is still being sent. Try again in a moment.'
    : 'No code has been sent from this screen yet. Tap "Resend code" to get one.';
}

/**
 * GoTrue's wording for a rejected code is opaque; say what to do instead.
 *
 * The factor type must be passed in rather than sniffed from the message:
 * GoTrue says "Invalid TOTP code" for a rejected PHONE code too, so keying the
 * advice off the text would send every SMS user to an app they never
 * installed.
 */
export function friendlyCodeError(message: string, factorType: FactorType): string {
  if (/invalid.*code|totp|verification/i.test(message)) {
    return factorType === 'phone'
      ? 'That code did not match. Check the text message and try again, or resend it.'
      : 'That code did not match. Check your authenticator app and try again.';
  }
  return message;
}

// ── wrappers ────────────────────────────────────────────────────────────────
// Thin. Each throws a plain Error carrying a message a vendor can read.

export async function getMfaState(client: SupabaseClient): Promise<MfaState> {
  const factorsRes = await client.auth.mfa.listFactors();
  if (factorsRes.error) throw new Error(factorsRes.error.message);

  const aalRes = await client.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalRes.error) throw new Error(aalRes.error.message);

  return deriveMfaState(
    (factorsRes.data?.all ?? []) as FactorLike[],
    aalRes.data?.currentLevel ?? null,
  );
}

export interface EnrollResult {
  factorId: string;
  qrCode: string;
  secret: string;
  uri: string;
}

/**
 * Begin TOTP enrolment. The factor is UNVERIFIED until verifyEnroll succeeds.
 * friendlyName must be unique per user, which is why cleanupUnverified runs
 * first at every call site.
 */
export async function enrollTotp(
  client: SupabaseClient,
  friendlyName = 'Authenticator',
): Promise<EnrollResult> {
  const { data, error } = await client.auth.mfa.enroll({ factorType: 'totp', friendlyName });
  if (error || !data) throw new Error(error?.message ?? 'Could not start two-factor setup');
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
    uri: data.totp.uri,
  };
}

/** Confirm a fresh factor with its first code. On success the session is aal2. */
export async function verifyEnroll(
  client: SupabaseClient,
  factorId: string,
  code: string,
): Promise<void> {
  const { error } = await client.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
  if (error) throw new Error(friendlyCodeError(error.message, 'totp'));
}

/** Prove an already-verified TOTP factor to lift this session to aal2. */
export async function stepUp(
  client: SupabaseClient,
  factorId: string,
  code: string,
): Promise<void> {
  const { error } = await client.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
  if (error) throw new Error(friendlyCodeError(error.message, 'totp'));
}

/** Begin SMS enrolment. Unverified until sendSmsChallenge + verifyPhone. */
export async function enrollPhone(
  client: SupabaseClient,
  phoneE164: string,
): Promise<{ factorId: string }> {
  const { data, error } = await client.auth.mfa.enroll({
    factorType: 'phone',
    phone: phoneE164.trim(),
  });
  if (error || !data) throw new Error(error?.message ?? 'Could not start SMS setup');
  return { factorId: data.id };
}

/**
 * Send an SMS code (both to confirm enrolment and to step up). Returns the
 * challengeId the code must be verified against — they are paired, and using
 * a challengeId from a different send is why a correct-looking code fails.
 */
export async function sendSmsChallenge(client: SupabaseClient, factorId: string): Promise<string> {
  const { data, error } = await client.auth.mfa.challenge({ factorId });
  if (error || !data) throw new Error(error?.message ?? 'Could not send the code');
  return data.id;
}

export async function verifyPhone(
  client: SupabaseClient,
  factorId: string,
  challengeId: string,
  code: string,
): Promise<void> {
  const { error } = await client.auth.mfa.verify({ factorId, challengeId, code: code.trim() });
  if (error) throw new Error(friendlyCodeError(error.message, 'phone'));
}

export async function unenroll(client: SupabaseClient, factorId: string): Promise<void> {
  const { error } = await client.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);
}

/**
 * Clear any half-finished factor before starting a fresh enrolment.
 *
 * Best-effort by design: if the list cannot be read this returns quietly
 * rather than throwing, because throwing would block the very enrolment it
 * exists to unblock.
 */
export async function cleanupUnverified(client: SupabaseClient): Promise<void> {
  const res = await client.auth.mfa.listFactors();
  if (res.error) return;
  const all = (res.data?.all ?? []) as FactorLike[];
  for (const factor of all.filter((f) => f.status === 'unverified')) {
    await client.auth.mfa.unenroll({ factorId: factor.id });
  }
}
